import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Statistic, Table, Typography, Spin, message, DatePicker, Button,Space } from 'antd';
import { AppstoreOutlined, BarChartOutlined, DislikeOutlined,DollarCircleOutlined , RiseOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../../../api/axios'; // Adjust path
import dayjs from 'dayjs';
import { Bar } from '@ant-design/plots';
import * as XLSX from 'xlsx';

const { Title, Text } = Typography;

// Reusable KPI Card
const StatCard = ({ title, value, prefix, loading }) => (
  <Card>
    <Spin spinning={loading}>
      <Statistic title={title} value={value} prefix={prefix} />
    </Spin>
  </Card>
);

const InventoryReport = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([dayjs().subtract(30, 'days'), dayjs()]);
  const [messageApi, contextHolder] = message.useMessage();

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange) {
        params.append('startDate', dateRange[0].toISOString());
        params.append('endDate', dateRange[1].toISOString());
      }
      const { data } = await api.get(`/api/inventory-reports/stats?${params.toString()}`);
      setReportData(data);
    } catch (error) {
      console.error("Report fetch error:", error);
      messageApi.error("Failed to load inventory report data.");
    } finally {
      setLoading(false);
    }
  }, [dateRange, messageApi]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);
  
  const handleExport = () => {
    if (!reportData) {
        messageApi.warning('No data to export.');
        return;
    }
    const workbook = XLSX.utils.book_new();
    // Export each section to a different sheet
    const topStockSheet = XLSX.utils.json_to_sheet(reportData.topStockedProducts.map(p => ({'Part Name': p.sparepart.partName, 'Quantity': p.quantity})));
    const fastMovingSheet = XLSX.utils.json_to_sheet(reportData.fastMovingProducts.map(p => ({'Part Name': p.sparepart.partName, 'Transactions': p.transactionCount})));
    const nonMovingSheet = XLSX.utils.json_to_sheet(reportData.nonMovingProducts.map(p => ({'Part Name': p.sparepart.partName, 'Warehouse': p.warehouse.name, 'Quantity': p.quantity})));

    XLSX.utils.book_append_sheet(workbook, topStockSheet, "Top Stocked");
    XLSX.utils.book_append_sheet(workbook, fastMovingSheet, "Fast Moving");
    XLSX.utils.book_append_sheet(workbook, nonMovingSheet, "Non-Moving Stock");
    
    XLSX.writeFile(workbook, `Inventory_Report_${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const topStockedColumns = [
    { title: 'Part Name', dataIndex: ['sparepart', 'partName'], key: 'partName' },
    { title: 'Current Quantity', dataIndex: 'quantity', key: 'quantity', align: 'right' },
  ];

  const fastMovingColumns = [
    { title: 'Part Name', dataIndex: ['sparepart', 'partName'], key: 'partName' },
    { title: 'Transactions (30 Days)', dataIndex: 'transactionCount', key: 'transactions', align: 'right' },
  ];
  
  const nonMovingColumns = [
    { title: 'Part Name', dataIndex: ['sparepart', 'partName'], key: 'partName' },
    { title: 'Warehouse', dataIndex: ['warehouse', 'name'], key: 'warehouse' },
    { title: 'Untouched Quantity', dataIndex: 'quantity', key: 'quantity', align: 'right' },
  ];

  const barChartConfig = {
    data: reportData?.valueByWarehouse.map(item => ({
        warehouse: item.warehouse.name,
        value: item.totalItems, // Using item count as a proxy for value
    })) || [],
    xField: 'value',
    yField: 'warehouse',
    seriesField: 'warehouse',
    legend: { position: 'top-left' },
    color: ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd'],
  };

  return (
    <>
      {contextHolder}
      <div style={{ padding: '24px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
          <Col>
            <Title level={3} style={{ margin: 0 }}>Inventory Intelligence Report</Title>
            <Text type="secondary">Analysis of stock levels and movement for the last 30 days.</Text>
          </Col>
          <Col>
            <Space>
                <DatePicker.RangePicker value={dateRange} onChange={setDateRange} />
                <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport}>Export Report</Button>
            </Space>
          </Col>
        </Row>

        {/* KPI Row */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="Total Inventory Value (Proxy)" value={reportData?.kpis?.totalValue || 0} prefix={<DollarCircleOutlined />} loading={loading} />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="Total Items in Stock" value={reportData?.kpis?.totalItems || 0} prefix={<AppstoreOutlined />} loading={loading} />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <StatCard title="Distinct Products (SKUs)" value={reportData?.kpis?.distinctSkus || 0} prefix={<BarChartOutlined />} loading={loading} />
          </Col>
        </Row>

        {/* Chart and Top Tables */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} lg={12}>
            <Card title="Inventory Value by Warehouse">
                <Spin spinning={loading}>
                    {reportData ? <Bar {...barChartConfig} height={250} /> : <div style={{height: 250}} />}
                </Spin>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Fast-Moving Products (by Transactions)">
              <Table loading={loading} columns={fastMovingColumns} dataSource={reportData?.fastMovingProducts} pagination={false} rowKey="sparepartId" size="small" />
            </Card>
          </Col>
        </Row>

        {/* Non-Moving and Top Stocked */}
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={12}>
            <Card title="Non-Moving Stock (Dead Stock)" headStyle={{color: '#cf1322'}}>
              <Table loading={loading} columns={nonMovingColumns} dataSource={reportData?.nonMovingProducts} pagination={{pageSize: 5}} rowKey={(r) => `${r.sparepart.partNumber}-${r.warehouse.name}`} size="small" />
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Top Stocked Products (by Quantity)">
              <Table loading={loading} columns={topStockedColumns} dataSource={reportData?.topStockedProducts} pagination={false} rowKey="sparepart.partNumber" size="small" />
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default InventoryReport;
